import { useState, useEffect, useCallback, useRef } from 'react';
import { useChainId } from 'wagmi';
import {
    fetchTokenUsdPrice,
    fetchPodOracleTokenUsdPrice,
} from '@/vendor/coti-wallet-plugin';
import {
    AVALANCHE_C_CHAIN_ID,
    AVALANCHE_FUJI_CHAIN_ID,
    COTI_MAINNET_CHAIN_ID,
    COTI_TESTNET_CHAIN_ID,
    ETHEREUM_MAINNET_CHAIN_ID,
    SEPOLIA_CHAIN_ID,
    getPublicTokensForChain,
} from '@/chains';
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const ERROR_LOG_COOLDOWN_MS = 60_000;

/** Tokens that support dynamic USD amount limits in AmountInputModal. */
const PRICE_SYMBOLS = ['WETH', 'WBTC', 'WADA', 'COTI', 'USDT', 'USDC.e', 'gCOTI', 'NIGHT'] as const;

/** Portal In UI maximum notional (USD). */
export const PORTAL_IN_MAX_USD = 100_000;

/** Chains whose asset prices come from the local PoDPriceOracle instead of the COTI oracle. */
const POD_ORACLE_CHAIN_IDS = new Set<number>([
    SEPOLIA_CHAIN_ID,
    AVALANCHE_FUJI_CHAIN_ID,
    ETHEREUM_MAINNET_CHAIN_ID,
    AVALANCHE_C_CHAIN_ID,
]);

export interface TokenPriceMap {
    [symbol: string]: number;
}

export interface UseTokenPricesReturn {
    prices: TokenPriceMap | null;
    amountsForOneDollar: TokenPriceMap | null;
    /** Token amounts equivalent to {@link PORTAL_IN_MAX_USD}. */
    amountsForMaxUsd: TokenPriceMap | null;
    isLoading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

function resolveOracleChainId(chainId: number | undefined): number {
    if (chainId === COTI_MAINNET_CHAIN_ID || chainId === COTI_TESTNET_CHAIN_ID) {
        return chainId;
    }
    return COTI_TESTNET_CHAIN_ID;
}

/** Symbol → USD price entries for the connected chain (null price = unavailable). */
async function fetchPriceEntries(chainId: number | undefined): Promise<Array<readonly [string, number | null]>> {
    if (chainId != null && POD_ORACLE_CHAIN_IDS.has(chainId)) {
        const symbols = getPublicTokensForChain(chainId).map(token => token.symbol);
        return Promise.all(
            symbols.map(async (symbol) => {
                const price = await fetchPodOracleTokenUsdPrice(symbol, chainId);
                return [symbol, price] as const;
            }),
        );
    }

    const oracleChainId = resolveOracleChainId(chainId);
    return Promise.all(
        PRICE_SYMBOLS.map(async (symbol) => {
            const price = await fetchTokenUsdPrice(symbol, undefined, oracleChainId);
            return [symbol, price] as const;
        }),
    );
}

/**
 * Fetches USD prices for the connected chain's assets and derives token amounts
 * equivalent to $1.00 (min) and {@link PORTAL_IN_MAX_USD} (max). COTI chains
 * read the on-chain COTI price consumer (same source as bridge fees); Sepolia/Fuji
 * read the PoDPriceOracle deployed with the privacy portals.
 */
export const useTokenPrices = (): UseTokenPricesReturn => {
    const wagmiChainId = useChainId();
    const [prices, setPrices] = useState<TokenPriceMap | null>(null);
    const [amountsForOneDollar, setAmountsForOneDollar] = useState<TokenPriceMap | null>(null);
    const [amountsForMaxUsd, setAmountsForMaxUsd] = useState<TokenPriceMap | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const lastErrorLoggedAt = useRef(0);

    const fetchPrices = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const entries = await fetchPriceEntries(wagmiChainId);

            const priceMap: TokenPriceMap = {};
            for (const [symbol, price] of entries) {
                if (price != null && price > 0) {
                    priceMap[symbol] = price;
                }
            }

            if (Object.keys(priceMap).length === 0) {
                throw new Error('No token prices returned from on-chain oracle');
            }

            setPrices(priceMap);

            const amountsMin: TokenPriceMap = {};
            const amountsMax: TokenPriceMap = {};
            for (const [symbol, price] of Object.entries(priceMap)) {
                amountsMin[symbol] = 1 / price;
                amountsMax[symbol] = PORTAL_IN_MAX_USD / price;
            }
            setAmountsForOneDollar(amountsMin);
            setAmountsForMaxUsd(amountsMax);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to fetch prices';
            setError(message);
            setPrices(null);
            setAmountsForOneDollar(null);
            setAmountsForMaxUsd(null);

            const now = Date.now();
            if (now - lastErrorLoggedAt.current >= ERROR_LOG_COOLDOWN_MS) {
                lastErrorLoggedAt.current = now;
                console.warn(
                    '[useTokenPrices] Oracle unavailable — using static portal amount limits.',
                    message,
                );
            }
        } finally {
            setIsLoading(false);
        }
    }, [wagmiChainId]);

    useEffect(() => {
        fetchPrices();

        const interval = setInterval(fetchPrices, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchPrices]);

    return {
        prices,
        amountsForOneDollar,
        amountsForMaxUsd,
        isLoading,
        error,
        refresh: fetchPrices,
    };
};
